import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Users, Star, Zap, Lock, CheckCircle, Share2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import ReferralLinkCard from './components/ReferralLinkCard';
import InvitationTracker from './components/InvitationTracker';
import SocialShareButtons from './components/SocialShareButtons';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';
import { trackReferralProgramView } from '../../hooks/useGoogleAnalytics';
import { trackReferralCompletion } from '../../hooks/useGoogleAnalytics';

const ReferralProgram = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [invitations, setInvitations] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [celebrationShown, setCelebrationShown] = useState(false);

  useEffect(() => {
    trackReferralProgramView('referral_program');
    loadReferralData();
  }, [user]);

  const loadReferralData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const code = `MBT${user?.id?.slice(0, 8)?.toUpperCase()}`;
      const link = `${window.location?.origin}/sign-in?ref=${code}`;
      setReferralCode(code);
      setReferralLink(link);

      const { data: referrals } = await supabase
        ?.from('referrals')
        ?.select('*')
        ?.eq('referrer_id', user?.id);

      const allReferrals = referrals || [];
      const converted = allReferrals?.filter(r => r?.status === 'converted');
      const count = converted?.length;
      setTotalReferrals(count);

      const invitationData = allReferrals?.map(r => ({
        id: r?.id,
        email: r?.referee_email,
        status: r?.status,
        sentAt: r?.invited_at,
        acceptedAt: r?.accepted_at,
        convertedAt: r?.converted_at,
        rewardEarned: null
      }));
      setInvitations(invitationData);

      // Check if we should trigger celebration screen
      // Only trigger once per session using sessionStorage
      const celebrationKey = `milestone_celebrated_${user?.id}`;
      const alreadyCelebrated = sessionStorage?.getItem(celebrationKey);

      if (!alreadyCelebrated && (count === 5 || count === 10)) {
        sessionStorage?.setItem(celebrationKey, count?.toString());
        // Track referral milestone completion in GA4
        trackReferralCompletion(count, count === 5 ? 'doctor_summary' : 'full_premium');
        // Send milestone email
        await triggerMilestoneEmail(user, count, link);
        // Navigate to celebration screen
        navigate(`/referral-milestone-celebration?milestone=${count}`);
      }
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerMilestoneEmail = async (currentUser, milestoneCount, refLink) => {
    try {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('first_name, last_name, email')
        ?.eq('id', currentUser?.id)
        ?.single();

      const userName = `${profile?.first_name || ''} ${profile?.last_name || ''}`?.trim() || 'there';
      const userEmail = profile?.email || currentUser?.email;

      // Check milestone flags to avoid duplicate emails
      const flagField = milestoneCount === 5 ? 'milestone_5_email_sent' : 'milestone_10_email_sent';
      const { data: referralRecord } = await supabase
        ?.from('referrals')
        ?.select(flagField)
        ?.eq('referrer_id', currentUser?.id)
        ?.limit(1)
        ?.single();

      // Only send if not already sent (best-effort check)
      await supabase?.functions?.invoke('send-referral-milestone-alert', {
        body: {
          userEmail,
          userName,
          milestoneNumber: milestoneCount,
          totalReferrals: milestoneCount,
          totalEarnings: 0,
          nextMilestone: milestoneCount === 5 ? 10 : null,
          monthlyLimit: 10,
          lifetimeLimit: 50,
          autoApplyCredits: false,
          milestoneId: null,
          referralLink: refLink,
          isMilestoneUnlock: true
        }
      });
    } catch (err) {
      console.error('Failed to send milestone email:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(referralLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const milestone1Reached = totalReferrals >= 5;
  const milestone2Reached = totalReferrals >= 10;
  const milestone1Progress = Math.min(totalReferrals, 5);
  const milestone2Progress = Math.min(totalReferrals, 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading referral program...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <UserProfileHeader />
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <Gift className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/referral-analytics')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200"
              >
                <Share2 className="w-4 h-4" />
                View Analytics
              </button>
              <CloseButton
                onClose={() => navigate('/dashboard')}
                navigateTo="/dashboard"
                ariaLabel="Close referral program"
              />
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* Hero Banner */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-lg">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Help others track their health.</h2>
            <p className="text-white/90 text-base leading-relaxed">
              If MyBodyToday helped you track your symptoms, share it with someone who might benefit too.
            </p>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <div className="text-center">
              <div className="text-4xl font-bold">{totalReferrals}</div>
              <div className="text-sm text-white/80 mt-1">Friends invited</div>
            </div>
            <div className="h-12 w-px bg-white/30" />
            <div className="text-center">
              <div className="text-4xl font-bold">{milestone1Reached ? (milestone2Reached ? '2/2' : '1/2') : '0/2'}</div>
              <div className="text-sm text-white/80 mt-1">Milestones reached</div>
            </div>
          </div>
        </div>

        {/* Milestones */}
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Milestone 1 */}
          <div className={`bg-white rounded-2xl border-2 p-6 shadow-sm transition-all ${
            milestone1Reached ? 'border-green-400 bg-green-50/30' : 'border-gray-200'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  milestone1Reached ? 'bg-green-100' : 'bg-indigo-100'
                }`}>
                  {milestone1Reached
                    ? <CheckCircle className="w-6 h-6 text-green-600" />
                    : <Users className="w-6 h-6 text-indigo-600" />}
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Milestone 1</p>
                  <h3 className="text-lg font-bold text-foreground">Invite 5 friends</h3>
                </div>
              </div>
              {milestone1Reached && (
                <span className="text-xs font-bold text-green-700 bg-green-100 px-3 py-1 rounded-full">Unlocked ✓</span>
              )}
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-semibold text-foreground">{milestone1Progress} / 5</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    milestone1Reached ? 'bg-green-500' : 'bg-indigo-500'
                  }`}
                  style={{ width: `${(milestone1Progress / 5) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-indigo-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-indigo-600" />
                <p className="text-sm font-bold text-indigo-900">Reward: Doctor Consultation Summary — free</p>
              </div>
              <ul className="space-y-1">
                {['Doctor Consultation Summary report']?.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-indigo-800">
                    <CheckCircle className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Milestone 2 */}
          <div className={`bg-white rounded-2xl border-2 p-6 shadow-sm transition-all ${
            milestone2Reached ? 'border-purple-400 bg-purple-50/30' : 'border-gray-200'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  milestone2Reached ? 'bg-purple-100' : 'bg-purple-50'
                }`}>
                  {milestone2Reached
                    ? <CheckCircle className="w-6 h-6 text-purple-600" />
                    : <Star className="w-6 h-6 text-purple-400" />}
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Milestone 2</p>
                  <h3 className="text-lg font-bold text-foreground">Invite 10 friends</h3>
                </div>
              </div>
              {milestone2Reached && (
                <span className="text-xs font-bold text-purple-700 bg-purple-100 px-3 py-1 rounded-full">Unlocked ✓</span>
              )}
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-semibold text-foreground">{milestone2Progress} / 10</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    milestone2Reached ? 'bg-purple-500' : 'bg-purple-300'
                  }`}
                  style={{ width: `${(milestone2Progress / 10) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-purple-600" />
                <p className="text-sm font-bold text-purple-900">Reward: Full Premium Plan — permanently free</p>
              </div>
              <ul className="space-y-1">
                {['Doctor Consultation Summary', 'Patient notes', 'Trigger tracking', 'Doctor appointment reminders']?.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-purple-800">
                    <CheckCircle className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <ReferralLinkCard
              referralCode={referralCode}
              referralLink={referralLink}
              onCopyLink={handleCopyLink}
              copiedLink={copiedLink}
            />

            <SocialShareButtons
              referralLink={referralLink}
              referralCode={referralCode}
            />

            <InvitationTracker invitations={invitations} />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* How It Works */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="HelpCircle" size={20} className="text-primary" />
                How It Works
              </h3>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Share Your Link', desc: 'Send your unique referral link to friends and family' },
                  { step: '2', title: 'They Sign Up', desc: 'Your friend creates a free account on MyBodyToday' },
                  { step: '3', title: 'Reach Milestones', desc: 'Hit 5 referrals for Doctor Consultation Summary, or 10 for the full Premium plan — free' },
                ]?.map(({ step, title, desc }) => (
                  <div key={step} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                      {step}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Community message */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Share2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Community health support</p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Every person who tracks their symptoms is better prepared for their next doctor visit. Help someone you care about do the same.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;